import { Inject } from '@odinjs/odin';

import { Endpoint, Return, SSOService, ToastService } from '@philips-emr/hpms-provider-commons';
import { EQUALS, equalsValidator, passwordEqualsValidator, PW_EQUALS } from '@philips-emr/hpms-provider-commons/lib/validators';
import { ButtonAPI, FieldAPI, FormAPI, ModalAPI, PanelAPI, ToastNotificationAPI } from '@philips-emr/tws-components';
import { ToastLevel } from '@philips-emr/tws-constants';
import { Value } from '@philips-emr/tws-core';
import Controller, { OnChange, OnClick, OnClose, OnMount, OnSubmit, OnValidate } from '@philips-emr/tws-core/lib/client';
import { MountEvent, SubmitEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, MessageService } from '@philips-emr/tws-core/lib/services';
import { RequestResponse } from '@philips-emr/tws-core/lib/services/http-service';
import { ModalService } from '@philips-emr/tws-core/lib/services/modal-service';

import { ChangePwAliases as Aliases, FragmentNames } from '../codegen';
import { Route } from '../route';

import BaseController from './BaseController';

interface User {
  response: string,
}

@Controller({ fragment: FragmentNames.CHANGE_PW })
export default class ChangePwController extends BaseController {

  @Inject(Aliases.FORM_CHANGE_PW)
  private formChangePassword: FormAPI;

  @Inject(Aliases.BUTTON_CHANGE_PW)
  private buttonChangePassword: ButtonAPI;

  @Inject(Aliases.TOAST_CHANGE_PW)
  private toastChangePassword: ToastNotificationAPI;

  @Inject(Aliases.PANEL_LOADER)
  private panelLoader: PanelAPI;

  @Inject(Aliases.PANEL_SUCCESS)
  private panelSuccess: PanelAPI;

  @Inject(Aliases.PANEL_MODAL)
  private panelModal: PanelAPI;

  @OnValidate(Aliases.FORM_CHANGE_PW)
  onValidate = ({ valid }: ValidateEvent): void => this.buttonChangePassword.setEnabled(valid);

  @OnMount(Aliases.MODAL)
  onMountModal(event: MountEvent): void {
    SSOService.ssoEnabled();
    const api = event.target as ModalAPI;
    const nrSeqWSuiteUser = this.context.get('nrSeqWSuiteUser');
    if (!nrSeqWSuiteUser) {
      api.setTitle(MessageService.translate('provider.change-password'));
    }
  }
  @OnMount(Aliases.FIELD_NEW_PW)
  onMountFieldNovaSenha({ target }: MountEvent): void {
    const api = target as FieldAPI;
    api.addLocalValidator(PW_EQUALS, (value: Value) => {
      const { currentPassword } = this.formChangePassword.getRecord();
      return passwordEqualsValidator(value as string, currentPassword as string);
    });
  }

  @OnMount(Aliases.FIELD_NEW_PW_CONFIRM)
  onMountFieldConfirmar({ target }: MountEvent): void {
    const api = target as FieldAPI;
    api.addLocalValidator(EQUALS, (value: Value) => {
      const { newPassword } = this.formChangePassword.getRecord();
      return equalsValidator(value as string, newPassword as string);
    });
  }

  @OnChange(Aliases.FORM_CHANGE_PW)
  onChangeForm(): void {
    this.toastChangePassword.setVisible(false);
  }

  @OnClick(Aliases.BUTTON_OK)
  onClickOk(): void {
    ModalService.close('renew-password');
  }

  @OnClose(Aliases.MODAL)
  onCloseModal(): void {
    ModalService.close('renew-password');
  }

  @OnClick(Aliases.BUTTON_CHANGE_PW)
  onClick(): void {
    const body = this.formChangePassword.getRecord();
    const nrSeqWSuiteUser = this.context.get('nrSeqWSuiteUser');
    if (nrSeqWSuiteUser) {
      HttpService.post(Endpoint.POST.RENEW_PW_BY_NRSEQ, { nrSeqWSuiteUser }, { body }).then(() => {
        this.handleSuccess();
      }).catch(resp => {
        this.handleFailure(resp);
      });
    } else {
      this.formChangePassword.submit();
    }
  }

  @OnSubmit(Aliases.FORM_CHANGE_PW)
  onSubmitFormAltPassword(event: SubmitEvent): void {
    const { record } = event;
    if (this.formChangePassword.isValid()) {
      this.buttonChangePassword.setEnabled(false);
      this.panelLoader.setVisible(true);
      HttpService.post<Return<string>>(Endpoint.POST.CHANGE_PW, {}, { body: record })
        .then(() => {
          this.panelLoader.setVisible(false);
          this.toastChangePassword.setLevel(ToastLevel.SUCCESS);
          this.toastChangePassword.setVisible(true);
          this.toastChangePassword.setMessage(`${MessageService.translate('provider.change-made-successfully')}`);
          this.panelModal.setVisible(false);

          this.historyService.push(Route.MY_DATA);
          ToastService.open({ message: 'provider.password-change-success', level: ToastLevel.SUCCESS });
        })
        .catch(resp => {
          const { body: message } = resp;
          if (message) {
            this.panelLoader.setVisible(false);
            this.toastChangePassword.setVisible(true);
            this.toastChangePassword.setMessage(message.response);
            this.formChangePassword.setEditing(true);
          }
        }).catch(() => ToastService.open({ message: MessageService.translate('provider.error-process-request'), level: ToastLevel.ERROR }));
    }
  }

  handleSuccess(): void {
    this.panelSuccess.setVisible(true);
    this.panelModal.setVisible(false);
  }

  handleFailure(resp: RequestResponse<unknown>): void {
    this.formChangePassword.setEditing(true);
    this.panelLoader.setVisible(false);
    const { response } = resp.body as User;
    this.toastChangePassword.setLevel(ToastLevel.WARNING);
    this.toastChangePassword.setVisible(true);
    this.toastChangePassword.setMessage(response);
  }

}
